import React, { useEffect, useState } from "react";
import { useAuthStore } from "../../stores/authStore";
import useDebounce from "../../hooks/useDebounce";
import { fetchCatalogProducts, CatalogProduct } from "./services/catalogAPI";
import AwsProductDetailModal from "./AwsProductDetailModal";
import SearchIcon from "@mui/icons-material/Search";
import styles from "./AwsProdutosCatalog.module.css";

const AwsProdutosCatalog: React.FC = () => {
  const { token } = useAuthStore();
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const [selectedProductRef, setSelectedProductRef] = useState<string | null>(
    null,
  );

  const itemsPerPage = 50;
  const debouncedSearch = useDebounce(searchTerm, 400);

  const loadProducts = async () => {
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchCatalogProducts({
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearch,
      });
      setProducts(response.products || []);
      setTotalItems(response.totalItems || 0);
    } catch (err: any) {
      console.error("Erro ao carregar catálogo:", err);
      setError("Falha ao carregar os produtos.");
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, [token, currentPage, debouncedSearch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <div className={styles.pageContainer}>
      <div className={styles.header}>
        <h1>Catálogo de Produtos Kadesh</h1>
        <a
          href="/catalogo.pdf"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            padding: "10px 20px",
            background: "#b91c1c",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: 700,
            fontSize: "0.9rem",
            textDecoration: "none",
            display: "inline-block",
          }}
        >
          📥 Catálogo PDF
        </a>
      </div>

      {/* ── Search Bar ── */}
      <div className={styles.searchBar}>
        <div className={styles.searchWrapper}>
          <SearchIcon className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Buscar por referência ou descrição..."
            value={searchTerm}
            onChange={handleSearchChange}
            className={styles.searchInput}
          />
        </div>
      </div>

      {error && (
        <p style={{ color: "#dc2626", textAlign: "center" }}>{error}</p>
      )}

      <div className={styles.tableCard}>
        <div className={styles.tableContainer}>
          <table className={styles.catalogTable}>
            <thead>
              <tr>
                <th style={{ width: "120px" }}>Cód / Ref</th>
                <th>Descrição</th>
                <th style={{ width: "130px" }}>Linha</th>
                <th style={{ width: "80px" }}>CA</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={4} className={styles.loadingCell}>
                    Carregando...
                  </td>
                </tr>
              ) : products.length > 0 ? (
                products.map((product) => (
                  <tr
                    key={product.ID}
                    onClick={() => setSelectedProductRef(product.REFERENCIA)}
                  >
                    <td>
                      <div className={styles.cellRef}>{product.REFERENCIA}</div>
                      <div className={styles.cellId}>#{product.ID}</div>
                    </td>
                    <td>
                      <span className={styles.cellDesc}>
                        {product.DESCRICAO}
                      </span>
                    </td>
                    <td>
                      <span className={styles.cellLinha}>{product.LINHA}</span>
                    </td>
                    <td>
                      <span className={styles.cellCA}>{product.CA}</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className={styles.emptyCell}>
                    Nenhum produto encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              className={styles.pageButton}
            >
              ‹ Anterior
            </button>
            <span className={styles.pageInfo}>
              Página {currentPage} de {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              className={styles.pageButton}
            >
              Próxima ›
            </button>
          </div>
        )}
      </div>

      {selectedProductRef && (
        <AwsProductDetailModal
          productRef={selectedProductRef}
          onClose={() => setSelectedProductRef(null)}
        />
      )}
    </div>
  );
};

export default AwsProdutosCatalog;
